import { useEffect, useMemo, useState } from 'react';
import { Calendar, Pencil } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { rangeHasTimeOfDay, useFilters, type RangePreset } from '@/state/filters';
import { fmt, TIMEZONE } from '@/lib/format';

const PRESET_OPTIONS: { value: Exclude<RangePreset, 'custom'>; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This week (Sun–Sat)' },
  { value: 'last-week', label: 'Last week (Sun–Sat)' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'mtd', label: 'Month to date' },
];

function customRangeTriggerLabel(from: Date, to: Date, withTime: boolean): string {
  const fy = formatInTimeZone(from, TIMEZONE, 'yyyy');
  const ty = formatInTimeZone(to, TIMEZONE, 'yyyy');
  if (withTime) {
    return `${fmt.date(from, 'd MMM')} ${fmt.date(from, 'HH:mm')} – ${fmt.date(to, 'd MMM')} ${fmt.date(to, 'HH:mm')}`;
  }
  if (fy === ty) {
    return `${fmt.date(from, 'd MMM')} – ${fmt.date(to, 'd MMM yyyy')}`;
  }
  return `${fmt.date(from, 'd MMM yyyy')} – ${fmt.date(to, 'd MMM yyyy')}`;
}

export function DateRangePicker() {
  const { range, setPreset, setCustomFromYmd, setCustomFromDateTime } = useFilters();
  const [customOpen, setCustomOpen] = useState(false);
  const [withTime, setWithTime] = useState(false);
  const [fromYmd, setFromYmd] = useState('');
  const [toYmd, setToYmd] = useState('');
  const [fromDateTime, setFromDateTime] = useState('');
  const [toDateTime, setToDateTime] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!customOpen) return;
    setFormError(null);
    const hasTime = range.preset === 'custom' && rangeHasTimeOfDay(range);
    setWithTime(hasTime);
    setFromYmd(formatInTimeZone(range.from, TIMEZONE, 'yyyy-MM-dd'));
    setToYmd(formatInTimeZone(range.to, TIMEZONE, 'yyyy-MM-dd'));
    setFromDateTime(formatInTimeZone(range.from, TIMEZONE, "yyyy-MM-dd'T'HH:mm"));
    setToDateTime(formatInTimeZone(range.to, TIMEZONE, "yyyy-MM-dd'T'HH:mm"));
  }, [customOpen, range, range.from, range.preset, range.to]);

  const triggerLabel = useMemo(() => {
    if (range.preset === 'custom') {
      return customRangeTriggerLabel(range.from, range.to, rangeHasTimeOfDay(range));
    }
    return PRESET_OPTIONS.find((o) => o.value === range.preset)?.label ?? 'Date range';
  }, [range]);

  const onSelectValue = (v: string) => {
    if (v === 'custom') {
      setCustomOpen(true);
      return;
    }
    setPreset(v as Exclude<RangePreset, 'custom'>);
  };

  const applyCustom = () => {
    setFormError(null);
    const ok = withTime
      ? setCustomFromDateTime(fromDateTime, toDateTime)
      : setCustomFromYmd(fromYmd, toYmd);
    if (!ok) {
      setFormError(
        withTime
          ? 'Enter valid start and end date+time values.'
          : 'Enter valid start and end dates.',
      );
      return;
    }
    setCustomOpen(false);
  };

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Calendar className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" aria-hidden />
      <Select value={range.preset} onValueChange={onSelectValue}>
        <SelectTrigger
          className="h-8 min-w-[140px] max-w-[240px] text-xs sm:min-w-[170px]"
          aria-label={`Date range: ${triggerLabel}`}
        >
          <span className="truncate text-left">{triggerLabel}</span>
        </SelectTrigger>
        <SelectContent>
          {PRESET_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
          <SelectItem value="custom">Custom range…</SelectItem>
        </SelectContent>
      </Select>
      {range.preset === 'custom' ? (
        <>
          {/* Wide screens: full clickable date range. */}
          <button
            type="button"
            className="hidden truncate text-left text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline xl:inline"
            onClick={() => setCustomOpen(true)}
            title="Edit custom range"
          >
            {fmt.date(range.from)} → {fmt.date(range.to)}
          </button>
          {/* Narrow screens: compact pencil-icon button so the affordance
              never disappears (the dropdown only shows "Custom range…",
              not the current values, so without this the user can't tell
              they're on a custom range or how to tweak it). */}
          <button
            type="button"
            className="press inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-input bg-surface text-muted-foreground hover:text-foreground focus-ring xl:hidden"
            onClick={() => setCustomOpen(true)}
            title={`Edit custom range (${fmt.date(range.from)} → ${fmt.date(range.to)})`}
            aria-label="Edit custom date range"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        </>
      ) : (
        <span className="hidden truncate text-xs text-muted-foreground xl:inline">
          {fmt.date(range.from)} → {fmt.date(range.to)}
        </span>
      )}

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle className="text-base font-semibold">
            Custom {withTime ? 'date + time' : 'date'} range
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Times use the <span className="font-mono text-foreground/90">{TIMEZONE}</span> timezone, matching charts and reports.
          </p>
          <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-xs">
            <span className="text-muted-foreground">Include time of day</span>
            <button
              type="button"
              role="switch"
              aria-checked={withTime}
              onClick={() => setWithTime((v) => !v)}
              className="press inline-flex h-7 items-center gap-2 rounded-md border border-border px-2.5 text-xs focus-ring"
            >
              <span
                aria-hidden
                className={`inline-block h-2 w-2 rounded-full ${withTime ? 'bg-success' : 'bg-muted-foreground/40'}`}
              />
              <span className="font-medium">{withTime ? 'on' : 'off'}</span>
            </button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {withTime ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bi-range-from-dt">From</Label>
                  <Input
                    id="bi-range-from-dt"
                    type="datetime-local"
                    value={fromDateTime}
                    onChange={(e) => setFromDateTime(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bi-range-to-dt">To</Label>
                  <Input
                    id="bi-range-to-dt"
                    type="datetime-local"
                    value={toDateTime}
                    onChange={(e) => setToDateTime(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bi-range-from">From</Label>
                  <Input id="bi-range-from" type="date" value={fromYmd} onChange={(e) => setFromYmd(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bi-range-to">To</Label>
                  <Input id="bi-range-to" type="date" value={toYmd} onChange={(e) => setToYmd(e.target.value)} />
                </div>
              </>
            )}
          </div>
          {formError ? <p className="text-xs text-danger">{formError}</p> : null}
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={applyCustom}
              disabled={withTime ? !fromDateTime || !toDateTime : !fromYmd || !toYmd}
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
